import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { FileText, Eye, Download, ArrowLeft } from 'lucide-react';
import './Documents.css';

interface GiepeiDoc {
  id: string;
  titulo: string;
  url: string;
  data: any;
  size?: number;
}

const Documents: React.FC = () => {
  const [docs, setDocs] = useState<GiepeiDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState<GiepeiDoc | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAccess = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/acesso');
        return;
      }

      try {
        // 1. Verificar si el email está en la lista de integrantes
        const qMembers = await getDocs(collection(db, 'integrantes_giepei'));
        const isMember = qMembers.docs.some(doc => doc.data().email?.toLowerCase() === user.email?.toLowerCase());

        if (!isMember) {
          alert('Acesso Restrito: Apenas integrantes do grupo GIEPEI podem visualizar estes documentos.');
          navigate('/perfil');
          return;
        }

        // 2. Cargar documentos
        const qDocs = query(collection(db, 'documentos_giepei'), orderBy('data', 'desc'));
        const snap = await getDocs(qDocs);
        setDocs(snap.docs.map(d => ({ id: d.id, ...d.data() } as GiepeiDoc)));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    });

    return () => checkAccess();
  }, [navigate]);

  if (loading) return <div className="container" style={{padding:'10rem 2rem', textAlign:'center'}}>Verificando permissões...</div>;

  return (
    <div className="documents-page container">
      <div className="docs-header">
        <button onClick={() => navigate('/perfil')} className="btn-back">
          <ArrowLeft size={18} /> Voltar ao Perfil
        </button>
        <h2>Repositório de Documentos GIEPEI</h2>
        <p className="docs-subtitle">Ficheiros e publicações internas para consulta.</p>
      </div>

      <div className="docs-layout">
        {/* Lista de Documentos */}
        <div className="docs-list-side">
          {docs.length === 0 ? (
            <p className="no-docs">Nenhum documento disponível no momento.</p>
          ) : (
            <div className="docs-grid">
              {docs.map(doc => (
                <div 
                  key={doc.id} 
                  className={`doc-card ${selectedDoc?.id === doc.id ? 'active' : ''}`}
                  onClick={() => setSelectedDoc(doc)}
                >
                  <div className="doc-icon"><FileText size={24} /></div>
                  <div className="doc-info">
                    <h4>{doc.titulo}</h4>
                    <span>{new Date(doc.data?.seconds * 1000).toLocaleDateString()}</span>
                  </div>
                  <div className="doc-actions-icons">
                    <Eye size={18} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Visor de Documentos */}
        <div className="docs-viewer-side">
          {selectedDoc ? (
            <div className="viewer-container">
              <div className="viewer-header">
                <h3>Visualizando: {selectedDoc.titulo}</h3>
                <a href={selectedDoc.url} target="_blank" rel="noopener noreferrer" className="btn-download-mini">
                  <Download size={16} /> Abrir em Nova Aba
                </a>
              </div>
              <div className="pdf-wrapper">
                <iframe 
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(selectedDoc.url)}&embedded=true`} 
                  title={selectedDoc.titulo}
                  width="100%" 
                  height="100%"
                  style={{ border: 'none' }}
                ></iframe>
              </div>
            </div>
          ) : (
            <div className="viewer-placeholder">
              <FileText size={48} />
              <p>Selecione um documento da lista para visualizar.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Documents;
